// WHAT: Tab component for Yazio smart sync functionality.
// WHY:  Provides interface for synchronizing Yazio nutrition data with local storage, supporting both today's data and custom date ranges with force sync options.
// DOMAIN: Health → Yazio
// DEPENDENCIES: React, ../../hooks/useYazioSync, ../../hooks/useYazioStoredData, @/components/ui/Button, @/components/ui/Card, @tracq/frontend-shared/utils
// RISKS:
//   - WARN: Sync operations are async; ensure proper loading state handling to prevent duplicate syncs.
//   - NOTE: Force sync bypasses cache; may cause unnecessary API calls and rate limiting.
//   - WARN: Stored data refresh after sync may fail silently; ensure error handling is comprehensive.

import React, { useState, useEffect } from "react";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { cn } from "@/shared/utils";

import { useYazioStoredData } from "../../hooks/use-yazio-stored-data";
import { useYazioSync } from "../../hooks/use-yazio-sync";

/**
 * WHAT:
 *   Tab component for Yazio smart sync functionality.
 *
 * WHY:
 *   Provides users with interface for synchronizing Yazio nutrition data with local storage,
 *   supporting both today's data and custom date ranges. Includes force sync options to
 *   bypass cache and refresh stored data after successful syncs.
 *
 * BEHAVIOR:
 *   - Displays sync status with color-coded indicators and icons
 *   - Provides sync buttons for today's data and custom date selection
 *   - Supports force sync mode to bypass cache
 *   - Shows last sync date and result information
 *   - Displays stored data information and refresh functionality
 *   - Handles sync errors with user-friendly messages
 *   - Automatically refreshes stored data after successful syncs
 *
 * RISKS:
 *   - WARN: Sync operations are async; ensure proper loading state handling to prevent duplicate syncs.
 *   - NOTE: Force sync bypasses cache; may cause unnecessary API calls and rate limiting.
 *   - WARN: Stored data refresh after sync may fail silently; ensure error handling is comprehensive.
 *   - NOTE: Date selection validation is minimal; invalid dates may cause sync failures.
 *
 * @returns {JSX.Element} Yazio sync tab component
 *
 */
export const YazioSyncTab: React.FC = () => {
  const {
    clearError,
    clearLastSync,
    error,
    forceSyncDate,
    forceSyncToday,
    isLoading,
    lastSyncDate,
    lastSyncResult,
    syncDateData,
    syncTodayData,
  } = useYazioSync();

  const {
    clearError: clearStoredError,
    data: storedData,
    error: storedError,
    isLoading: isLoadingStored,
    refreshData: refreshStoredData,
  } = useYazioStoredData();

  // NOTE: Debug effect hook for stored data (currently empty; may be used for logging)
  useEffect(() => {
    // Future: Add logging here if needed
    // Reason: Placeholder for future logging functionality - effect hook intentionally empty
  }, [storedData, isLoadingStored, storedError]);

  const [selectedDate, setSelectedDate] = useState<string>("");
  const [showForceSync, setShowForceSync] = useState(false);

  /**
   * WHAT:
   *   Handles syncing today's Yazio data.
   *
   * WHY:
   *   Provides user-initiated sync for today's nutrition data with automatic stored data refresh.
   *
   * SIDE EFFECTS:
   *   - Calls syncTodayData to fetch today's data
   *   - Refreshes stored data if sync is successful and data was synced
   *
   * RISKS:
   *   - WARN: Errors are silently caught; consider user feedback for failures.
   *   - NOTE: Stored data refresh depends on sync result; may not refresh if sync fails.
   */
  const handleSyncToday = async () => {
    try {
      const result = await syncTodayData();
      // NOTE: Refresh stored data after successful sync
      if (result.success && result.data_synced) {
        await refreshStoredData();
      }
    } catch {
      // NOTE: Sync failed - error is silently caught
    }
  };

  /**
   * WHAT:
   *   Handles syncing Yazio data for a selected date.
   *
   * WHY:
   *   Allows users to sync nutrition data for specific dates, not just today.
   *
   * SIDE EFFECTS:
   *   - Validates selectedDate is not empty
   *   - Calls syncDateData with selected date
   *   - Refreshes stored data if sync is successful and data was synced
   *
   * RISKS:
   *   - WARN: Date validation is minimal; invalid dates may cause sync failures.
   *   - NOTE: Errors are silently caught; consider user feedback for failures.
   */
  const handleSyncDate = async () => {
    if (!selectedDate) {
      return;
    }

    try {
      const result = await syncDateData(selectedDate);
      // NOTE: Refresh stored data after successful sync
      if (result.success && result.data_synced) {
        await refreshStoredData();
      }
    } catch {
      // NOTE: Sync failed - error is silently caught
    }
  };

  /**
   * WHAT:
   *   Handles force syncing today's Yazio data (bypasses cache).
   *
   * WHY:
   *   Allows users to force refresh today's data even if it was recently synced.
   *
   * SIDE EFFECTS:
   *   - Calls forceSyncToday to fetch today's data bypassing cache
   *   - Refreshes stored data if sync is successful and data was synced
   *
   * RISKS:
   *   - WARN: Force sync bypasses cache; may cause unnecessary API calls and rate limiting.
   *   - NOTE: Errors are silently caught; consider user feedback for failures.
   */
  const handleForceSyncToday = async () => {
    try {
      const result = await forceSyncToday();
      // NOTE: Refresh stored data after successful sync
      if (result.success && result.data_synced) {
        await refreshStoredData();
      }
    } catch {
      // WARN: Force sync failed - error is silently caught
    }
  };

  /**
   * WHAT:
   *   Handles force syncing Yazio data for a selected date (bypasses cache).
   *
   * WHY:
   *   Allows users to force refresh data for specific dates even if recently synced.
   *
   * SIDE EFFECTS:
   *   - Validates selectedDate is not empty
   *   - Calls forceSyncDate with selected date bypassing cache
   *   - Refreshes stored data if sync is successful and data was synced
   *
   * RISKS:
   *   - WARN: Force sync bypasses cache; may cause unnecessary API calls and rate limiting.
   *   - NOTE: Date validation is minimal; invalid dates may cause sync failures.
   *   - WARN: Errors are silently caught; consider user feedback for failures.
   */
  const handleForceSyncDate = async () => {
    if (!selectedDate) {
      return;
    }

    try {
      const result = await forceSyncDate(selectedDate);
      // NOTE: Refresh stored data after successful sync
      if (result.success && result.data_synced) {
        await refreshStoredData();
      }
    } catch {
      // WARN: Force sync failed - error is silently caught
    }
  };

  /**
   * WHAT:
   *   Returns color class based on last sync result status.
   *
   * WHY:
   *   Provides visual feedback for sync status (pending, success, partial success, failure).
   *
   * OUTPUT:
   *   - string - Tailwind CSS color class
   *
   * RISKS:
   *   - NOTE: Assumes lastSyncResult structure; may fail with unexpected data.
   *
   * @returns {string} Tailwind CSS color class
   *
   */
  const getSyncStatusColor = () => {
    if (!lastSyncResult) {
      return "text-gray-500";
    }
    if (lastSyncResult.success && lastSyncResult.data_synced) {
      return "text-green-500";
    }
    if (lastSyncResult.success && !lastSyncResult.data_synced) {
      return "text-yellow-500";
    }
    return "text-red-500";
  };

  /**
   * WHAT:
   *   Returns emoji icon based on last sync result status.
   *
   * WHY:
   *   Provides visual indicator for sync status (pending, success, partial success, failure).
   *
   * OUTPUT:
   *   - string - Emoji icon
   *
   * RISKS:
   *   - NOTE: Assumes lastSyncResult structure; may fail with unexpected data.
   *
   * @returns {string} Emoji icon
   *
   */
  const getSyncStatusIcon = () => {
    if (!lastSyncResult) {
      return "⏳";
    }
    if (lastSyncResult.success && lastSyncResult.data_synced) {
      return "✅";
    }
    if (lastSyncResult.success && !lastSyncResult.data_synced) {
      return "⚠️";
    }
    return "❌";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Smart Sync</h2>
        <Button
          className="text-xs"
          onClick={() => {
            setShowForceSync(!showForceSync);
          }}
          size="sm"
          variant="outline"
        >
          {showForceSync ? "Hide" : "Show"} Force Sync
        </Button>
      </div>

      {/* Sync Status */}
      {lastSyncResult ? (
        <Card className="p-4 bg-gray-800 border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <span className="text-lg">{getSyncStatusIcon()}</span>
              <span className={cn("font-medium", getSyncStatusColor())}>
                Last Sync: {lastSyncDate?.toLocaleString()}
              </span>
            </div>
            <Button
              className="text-gray-400 hover:text-white"
              onClick={clearLastSync}
              size="sm"
              variant="ghost"
            >
              Clear
            </Button>
          </div>

          <div className="space-y-2">
            <p className="text-sm text-gray-300">
              <strong>Date:</strong> {lastSyncResult.date}
            </p>
            <p className="text-sm text-gray-300">
              <strong>Status:</strong> {lastSyncResult.message}
            </p>

            {lastSyncResult.data_synced ? (
              <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-gray-700">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-400">
                    {lastSyncResult.total_calories.toFixed(0)}
                  </p>
                  <p className="text-xs text-gray-400">Total Calories</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-400">
                    {lastSyncResult.protein.toFixed(1)}g
                  </p>
                  <p className="text-xs text-gray-400">Protein</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-400">
                    {lastSyncResult.fat.toFixed(1)}g
                  </p>
                  <p className="text-xs text-gray-400">Fat</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-400">
                    {lastSyncResult.carbs.toFixed(1)}g
                  </p>
                  <p className="text-xs text-gray-400">Carbs</p>
                </div>
              </div>
            ) : null}
          </div>
        </Card>
      ) : null}

      {/* Error Display */}
      {error ? (
        <Card className="p-4 bg-red-900/20 border-red-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-red-400">❌</span>
              <span className="text-red-300">{error}</span>
            </div>
            <Button
              className="text-red-400 hover:text-red-300"
              onClick={clearError}
              size="sm"
              variant="ghost"
            >
              Dismiss
            </Button>
          </div>
        </Card>
      ) : null}

      {/* Sync Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Today's Data */}
        <Card className="p-6 bg-gray-800 border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Today&apos;s Data</h3>
          <div className="space-y-3">
            <Button
              className="w-full"
              disabled={isLoading}
              onClick={() => {
                void handleSyncToday();
              }}
              variant="primary"
            >
              {isLoading ? "Syncing..." : "Sync Today"}
            </Button>

            {showForceSync ? (
              <Button
                className="w-full"
                disabled={isLoading}
                onClick={() => {
                  void handleForceSyncToday();
                }}
                variant="danger"
              >
                Force Sync Today
              </Button>
            ) : null}
          </div>
        </Card>

        {/* Specific Date */}
        <Card className="p-6 bg-gray-800 border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Specific Date</h3>
          <div className="space-y-3">
            <input
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={(e) => {
                setSelectedDate(e.target.value);
              }}
              type="date"
              value={selectedDate}
            />

            <Button
              className="w-full"
              disabled={isLoading || !selectedDate}
              onClick={() => {
                void handleSyncDate();
              }}
              variant="secondary"
            >
              {isLoading ? "Syncing..." : "Sync Date"}
            </Button>

            {showForceSync ? (
              <Button
                className="w-full"
                disabled={isLoading || !selectedDate}
                onClick={() => {
                  void handleForceSyncDate();
                }}
                variant="danger"
              >
                Force Sync Date
              </Button>
            ) : null}
          </div>
        </Card>
      </div>

      {/* Stored Data Overview */}
      <Card className="p-6 bg-gray-800 border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Stored Data</h3>
          <Button
            disabled={isLoadingStored}
            onClick={() => {
              void refreshStoredData();
            }}
            size="sm"
            variant="outline"
          >
            {isLoadingStored ? "Loading..." : "Refresh"}
          </Button>
        </div>

        {storedError ? (
          <div className="mb-4 p-3 bg-red-900/20 border border-red-700 rounded-md">
            <div className="flex items-center justify-between">
              <span className="text-red-300 text-sm">{storedError}</span>
              <Button
                className="text-red-400 hover:text-red-300"
                onClick={clearStoredError}
                size="sm"
                variant="ghost"
              >
                Dismiss
              </Button>
            </div>
          </div>
        ) : null}

        {storedData && storedData.data.length > 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-400">{storedData.count} entries stored in database</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {storedData.data.slice(0, 6).map((entry) => (
                <div className="p-3 bg-gray-700 rounded-md border border-gray-600" key={entry.id}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-white">
                      {new Date(entry.date).toLocaleDateString()}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(entry.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Calories:</span>
                      <span className="text-blue-400 font-medium">
                        {entry.total_calories.toFixed(0)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Protein:</span>
                      <span className="text-green-400">{entry.protein.toFixed(1)}g</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Fat:</span>
                      <span className="text-yellow-400">{entry.fat.toFixed(1)}g</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Carbs:</span>
                      <span className="text-purple-400">{entry.carbs.toFixed(1)}g</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {storedData.data.length > 6 && (
              <p className="text-xs text-gray-500 text-center">
                Showing first 6 of {storedData.count} entries
              </p>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-400 mb-2">No stored data found</p>
            <p className="text-sm text-gray-500">Sync some data to see it here</p>
          </div>
        )}
      </Card>

      {/* Smart Sync Info */}
      <Card className="p-6 bg-blue-900/20 border-blue-700">
        <h3 className="text-lg font-semibold text-blue-300 mb-3">Smart Sync Logic</h3>
        <div className="space-y-2 text-sm text-blue-200">
          <p>
            • <strong>Data Validation:</strong> Only syncs if data is complete and meaningful
          </p>
          <p>
            • <strong>Duplicate Prevention:</strong> Skips if data already exists (unless force
            sync)
          </p>
          <p>
            • <strong>Quality Check:</strong> Ensures goals are set and food is logged
          </p>
          <p>
            • <strong>Force Sync:</strong> Overwrites existing data when needed
          </p>
        </div>
      </Card>
    </div>
  );
};
